import { useState, useEffect } from 'react'
import { subscribeToJobRoleAnalysis, createJobRoleAnalysis, deleteJobRoleAnalysis, updateJobRoleAnalysis } from '../firebase/jobRoleAnalysis'
import './JobRoleAnalysis.css'

const JobRoleAnalysis = ({ user }) => {
  const [jobs, setJobs] = useState([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [editingJob, setEditingJob] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState('')
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, jobId: null, jobTitle: '' })
  const [sortColumn, setSortColumn] = useState(null)
  const [sortDirection, setSortDirection] = useState('asc')

  const categories = ['AI Security', 'SaaS Security', 'Risk Management']

  useEffect(() => {
    if (!user) return

    // Subscribe to real-time job role analysis updates (shared resource)
    const unsubscribeJobs = subscribeToJobRoleAnalysis((jobsData) => {
      setJobs(jobsData)
      setLoading(false)
    })

    return () => {
      unsubscribeJobs()
    }
  }, [user])

  const handleCreateJob = async (jobData) => {
    try {
      await createJobRoleAnalysis(jobData)
      setShowForm(false)
      setEditingJob(null)
    } catch (error) {
      console.error('Error creating job role analysis:', error)
      alert('Failed to create job role analysis entry. Please try again.')
    }
  }

  const handleUpdateJob = async (jobData) => {
    try {
      await updateJobRoleAnalysis(editingJob.id, jobData)
      setShowForm(false)
      setEditingJob(null)
    } catch (error) {
      console.error('Error updating job role analysis:', error)
      alert('Failed to update job role analysis entry. Please try again.')
    }
  }

  const handleEditJob = (job) => {
    setEditingJob(job)
    setShowForm(true)
  }

  const handleDeleteJob = async (jobId, e) => {
    e?.preventDefault?.()
    e?.stopPropagation?.()
    
    const job = jobs.find(j => j.id === jobId)
    const jobTitle = job?.jobTitle || 'this job'
    
    setDeleteConfirm({ show: true, jobId, jobTitle })
  }

  const confirmDelete = async () => {
    const { jobId } = deleteConfirm
    
    try {
      await deleteJobRoleAnalysis(jobId)
      setDeleteConfirm({ show: false, jobId: null, jobTitle: '' })
      alert('✅ Job role analysis entry deleted successfully')
    } catch (error) {
      console.error('Error deleting job role analysis:', error)
      alert(`❌ Failed to delete entry: ${error.message || 'Unknown error'}`)
      setDeleteConfirm({ show: false, jobId: null, jobTitle: '' })
    }
  }

  const cancelDelete = () => {
    setDeleteConfirm({ show: false, jobId: null, jobTitle: '' })
  }

  const formatDate = (timestamp) => {
    if (!timestamp) return '-'
    
    let date
    if (timestamp.toDate) {
      date = timestamp.toDate()
    } else if (timestamp instanceof Date) {
      date = timestamp
    } else if (timestamp.seconds) {
      date = new Date(timestamp.seconds * 1000)
    } else {
      return '-'
    }
    
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  // Filter jobs
  const filteredJobs = jobs.filter((job) => {
    const searchLower = searchTerm.toLowerCase()
    const matchesSearch = !searchTerm || (
      job.jobTitle?.toLowerCase().includes(searchLower) ||
      job.company?.toLowerCase().includes(searchLower) ||
      job.source?.toLowerCase().includes(searchLower) ||
      job.category?.toLowerCase().includes(searchLower) ||
      (job.skills || []).some(skill => skill.toLowerCase().includes(searchLower)) ||
      (job.certifications || []).some(cert => cert.toLowerCase().includes(searchLower)) ||
      job.notes?.toLowerCase().includes(searchLower)
    )
    
    const matchesCategory = !selectedCategoryFilter || job.category === selectedCategoryFilter
    
    return matchesSearch && matchesCategory
  })

  // Sort function
  const sortJobs = (jobsToSort, column, direction) => {
    if (!column) return jobsToSort

    return [...jobsToSort].sort((a, b) => {
      let aValue = a[column]
      let bValue = b[column]

      if (column === 'createdAt' || column === 'updatedAt') {
        const getDate = (timestamp) => {
          if (!timestamp) return new Date(0)
          if (timestamp.toDate) return timestamp.toDate()
          if (timestamp instanceof Date) return timestamp
          if (timestamp.seconds) return new Date(timestamp.seconds * 1000)
          return new Date(0)
        }
        aValue = getDate(aValue)
        bValue = getDate(bValue)
      } else {
        aValue = aValue || ''
        bValue = bValue || ''
        aValue = String(aValue).toLowerCase()
        bValue = String(bValue).toLowerCase()
      }

      let comparison = 0
      if (aValue < bValue) {
        comparison = -1
      } else if (aValue > bValue) {
        comparison = 1
      }

      return direction === 'asc' ? comparison : -comparison
    })
  }

  const sortedJobs = sortJobs(filteredJobs, sortColumn, sortDirection)

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortColumn(column)
      setSortDirection('asc')
    }
  }

  if (loading) {
    return <div className="loading">Loading job role analysis...</div>
  }

  return (
    <div className="job-role-analysis-page">
      <div className="page-header">
        <div>
          <h2>Job Role Analysis</h2>
          <p className="subtitle">Review job postings and extract skills, certifications, and responsibilities • {jobs.length} total entries</p>
        </div>
        <div className="header-actions">
          <button className="btn-primary" onClick={() => setShowForm(true)}>
            + New Entry
          </button>
        </div>
      </div>

      <div className="filters-bar">
        <div className="search-bar">
          <input
            type="text"
            placeholder="Search by job title, company, source, category, skills, certifications, or notes..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        <div className="category-filter">
          <select
            value={selectedCategoryFilter}
            onChange={(e) => setSelectedCategoryFilter(e.target.value)}
            className="category-filter-select"
          >
            <option value="">All Categories</option>
            {categories.map(category => (
              <option key={category} value={category}>{category}</option>
            ))}
          </select>
        </div>
      </div>

      {showForm && (
        <JobForm
          onSubmit={editingJob ? handleUpdateJob : handleCreateJob}
          onCancel={() => {
            setShowForm(false)
            setEditingJob(null)
          }}
          categories={categories}
          initialData={editingJob}
        />
      )}

      {sortedJobs.length === 0 ? (
        <div className="empty-state">
          <p>{searchTerm || selectedCategoryFilter ? 'No entries match your filters.' : 'No entries yet. Add your first job role analysis entry!'}</p>
        </div>
      ) : (
        <div className="jobs-table-container">
          <table className="jobs-table">
            <thead>
              <tr>
                <th className="sortable" onClick={() => handleSort('jobTitle')}>
                  <span>Job Title</span>
                  {sortColumn === 'jobTitle' && (
                    <span className="sort-indicator">
                      {sortDirection === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </th>
                <th className="sortable" onClick={() => handleSort('company')}>
                  <span>Company</span>
                  {sortColumn === 'company' && (
                    <span className="sort-indicator">
                      {sortDirection === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </th>
                <th className="sortable" onClick={() => handleSort('source')}>
                  <span>Source</span>
                  {sortColumn === 'source' && (
                    <span className="sort-indicator">
                      {sortDirection === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </th>
                <th className="sortable" onClick={() => handleSort('category')}>
                  <span>Category</span>
                  {sortColumn === 'category' && (
                    <span className="sort-indicator">
                      {sortDirection === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </th>
                <th>Skills</th>
                <th>Certifications</th>
                <th>Responsibilities</th>
                <th>Notes</th>
                <th className="sortable" onClick={() => handleSort('createdAt')}>
                  <span>Created</span>
                  {sortColumn === 'createdAt' && (
                    <span className="sort-indicator">
                      {sortDirection === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedJobs.map((job) => (
                <tr key={job.id}>
                  <td className="job-title-cell">
                    {job.jobTitle || '-'}
                  </td>
                  <td>{job.company || '-'}</td>
                  <td>{job.source || '-'}</td>
                  <td>
                    <span className="category-badge">{job.category || '-'}</span>
                  </td>
                  <td className="list-cell">
                    {(job.skills || []).length > 0 ? (
                      <ul className="list-items">
                        {(job.skills || []).slice(0, 3).map((skill, idx) => (
                          <li key={idx}>{skill}</li>
                        ))}
                        {(job.skills || []).length > 3 && (
                          <li className="more-items">+{(job.skills || []).length - 3} more</li>
                        )}
                      </ul>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td className="list-cell">
                    {(job.certifications || []).length > 0 ? (
                      <ul className="list-items">
                        {(job.certifications || []).slice(0, 3).map((cert, idx) => (
                          <li key={idx}>{cert}</li>
                        ))}
                        {(job.certifications || []).length > 3 && (
                          <li className="more-items">+{(job.certifications || []).length - 3} more</li>
                        )}
                      </ul>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td className="list-cell">
                    {(job.responsibilities || []).length > 0 ? (
                      <ul className="list-items">
                        {(job.responsibilities || []).slice(0, 2).map((resp, idx) => (
                          <li key={idx}>{resp}</li>
                        ))}
                        {(job.responsibilities || []).length > 2 && (
                          <li className="more-items">+{(job.responsibilities || []).length - 2} more</li>
                        )}
                      </ul>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td className="notes-cell">
                    {job.notes ? (
                      <span title={job.notes}>
                        {job.notes.length > 50 ? job.notes.substring(0, 50) + '...' : job.notes}
                      </span>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td title={formatDate(job.createdAt)}>
                    {formatDate(job.createdAt)}
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        type="button"
                        className="edit-btn"
                        onClick={(e) => {
                          e.preventDefault()
                          e.stopPropagation()
                          handleEditJob(job)
                        }}
                        title="Edit entry"
                      >
                        ✎
                      </button>
                      <button
                        type="button"
                        className="delete-btn"
                        onClick={(e) => handleDeleteJob(job.id, e)}
                        title="Delete entry"
                      >
                        ×
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {deleteConfirm.show && (
        <div className="delete-confirm-overlay">
          <div className="delete-confirm-modal">
            <h3>⚠️ Delete Entry</h3>
            <p>Are you sure you want to permanently delete <strong>{deleteConfirm.jobTitle}</strong>?</p>
            <p className="warning-text">This action cannot be undone.</p>
            <div className="delete-confirm-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={cancelDelete}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-danger"
                onClick={confirmDelete}
              >
                Delete Entry
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// Job Form Component
const JobForm = ({ onSubmit, onCancel, categories, initialData = null }) => {
  const [formData, setFormData] = useState({
    jobTitle: initialData?.jobTitle || '',
    company: initialData?.company || '',
    source: initialData?.source || 'LinkedIn',
    category: initialData?.category || 'AI Security',
    skills: initialData?.skills || [],
    certifications: initialData?.certifications || [],
    responsibilities: initialData?.responsibilities || [],
    notes: initialData?.notes || ''
  })
  const [skillInput, setSkillInput] = useState('')
  const [certInput, setCertInput] = useState('')
  const [respInput, setRespInput] = useState('')
  const [loading, setLoading] = useState(false)

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)

    try {
      await onSubmit(formData)
    } catch (error) {
      console.error('Error submitting form:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
  }

  const handleAddSkill = () => {
    if (skillInput.trim() && !formData.skills.includes(skillInput.trim())) {
      setFormData({
        ...formData,
        skills: [...formData.skills, skillInput.trim()]
      })
      setSkillInput('')
    }
  }

  const handleRemoveSkill = (skillToRemove) => {
    setFormData({
      ...formData,
      skills: formData.skills.filter(skill => skill !== skillToRemove)
    })
  }

  const handleAddCert = () => {
    if (certInput.trim() && !formData.certifications.includes(certInput.trim())) {
      setFormData({
        ...formData,
        certifications: [...formData.certifications, certInput.trim()]
      })
      setCertInput('')
    }
  }

  const handleRemoveCert = (certToRemove) => {
    setFormData({
      ...formData,
      certifications: formData.certifications.filter(cert => cert !== certToRemove)
    })
  }

  const handleAddResp = () => {
    if (respInput.trim() && !formData.responsibilities.includes(respInput.trim())) {
      setFormData({
        ...formData,
        responsibilities: [...formData.responsibilities, respInput.trim()]
      })
      setRespInput('')
    }
  }

  const handleRemoveResp = (respToRemove) => {
    setFormData({
      ...formData,
      responsibilities: formData.responsibilities.filter(resp => resp !== respToRemove)
    })
  }

  return (
    <div className="form-overlay">
      <div className="form-modal large-modal">
        <div className="form-header">
          <h3>{initialData ? 'Edit Job Role Analysis Entry' : 'New Job Role Analysis Entry'}</h3>
          <button className="close-btn" onClick={onCancel}>×</button>
        </div>
        
        <form onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="jobTitle">Job Title *</label>
              <input
                type="text"
                id="jobTitle"
                name="jobTitle"
                value={formData.jobTitle}
                onChange={handleChange}
                required
                placeholder="AI Security Engineer"
              />
            </div>

            <div className="form-group">
              <label htmlFor="company">Company *</label>
              <input
                type="text"
                id="company"
                name="company"
                value={formData.company}
                onChange={handleChange}
                required
                placeholder="Acme Inc."
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="source">Source *</label>
              <select
                id="source"
                name="source"
                value={formData.source}
                onChange={handleChange}
                required
              >
                <option value="LinkedIn">LinkedIn</option>
                <option value="Indeed">Indeed</option>
                <option value="Company Site">Company Site</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="category">Category *</label>
              <select
                id="category"
                name="category"
                value={formData.category}
                onChange={handleChange}
                required
              >
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="skills">Skills</label>
            <div className="list-input-group">
              <input
                type="text"
                id="skills"
                value={skillInput}
                onChange={(e) => setSkillInput(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    handleAddSkill()
                  }
                }}
                placeholder="Enter a skill and press Enter"
              />
              <button type="button" className="btn-secondary" onClick={handleAddSkill}>
                Add
              </button>
            </div>
            {formData.skills.length > 0 && (
              <div className="selected-items">
                {formData.skills.map((skill, idx) => (
                  <span key={idx} className="item-tag">
                    {skill}
                    <button
                      type="button"
                      className="item-remove"
                      onClick={() => handleRemoveSkill(skill)}
                      title="Remove skill"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="certifications">Certifications</label>
            <div className="list-input-group">
              <input
                type="text"
                id="certifications"
                value={certInput}
                onChange={(e) => setCertInput(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    handleAddCert()
                  }
                }}
                placeholder="Enter a certification and press Enter"
              />
              <button type="button" className="btn-secondary" onClick={handleAddCert}>
                Add
              </button>
            </div>
            {formData.certifications.length > 0 && (
              <div className="selected-items">
                {formData.certifications.map((cert, idx) => (
                  <span key={idx} className="item-tag">
                    {cert}
                    <button
                      type="button"
                      className="item-remove"
                      onClick={() => handleRemoveCert(cert)}
                      title="Remove certification"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="responsibilities">Responsibilities</label>
            <div className="list-input-group">
              <input
                type="text"
                id="responsibilities"
                value={respInput}
                onChange={(e) => setRespInput(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    handleAddResp()
                  }
                }}
                placeholder="Enter a responsibility and press Enter"
              />
              <button type="button" className="btn-secondary" onClick={handleAddResp}>
                Add
              </button>
            </div>
            {formData.responsibilities.length > 0 && (
              <div className="selected-items">
                {formData.responsibilities.map((resp, idx) => (
                  <span key={idx} className="item-tag">
                    {resp}
                    <button
                      type="button"
                      className="item-remove"
                      onClick={() => handleRemoveResp(resp)}
                      title="Remove responsibility"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="notes">Notes</label>
            <textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows="4"
              placeholder="Additional observations or insights..."
            />
          </div>

          <div className="form-actions">
            <button type="button" className="btn-secondary" onClick={onCancel}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? 'Saving...' : (initialData ? 'Update Entry' : 'Create Entry')}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default JobRoleAnalysis
